import React from "react";

// Canonical: 10 units = 1 metre. Yard conversion: 1 yd = 0.9144 m.
const UNITS_PER_M = 10;
const M_PER_YD = 0.9144;
const toM = (u) => u / UNITS_PER_M;
const toYd = (u) => toM(u) / M_PER_YD;
const fmt = (v) => (v >= 10 ? v.toFixed(0) : v.toFixed(1));

// Compute bounding box for a shape in canonical space.
const shapeBBox = (s) => {
  if (s.type === "rect") return { x: s.x, y: s.y, w: s.w, h: s.h };
  if (s.type === "circle") return { x: s.cx - s.r, y: s.cy - s.r, w: s.r * 2, h: s.r * 2 };
  return { x: 0, y: 0, w: 0, h: 0 };
};

export const PitchShape = ({
  shape,
  isSelected,
  showHandles,
  showDimensions,
  pitchRotation = 0,
  onPointerDown,
  onHandlePointerDown,
  onDimPointerDown,
}) => {
  const stroke = shape.color || "#0F172A";
  const strokeIsWhite = stroke === "#FFFFFF" || stroke.toLowerCase() === "white";
  const sw = 3;
  // Colored fills tint the shape lightly. White strokes look wrong against
  // the pitch without a hint of contrast, so pair with a slate outline halo.
  const fillHex = strokeIsWhite ? "rgba(255,255,255,0.10)" : "rgba(15, 23, 42, 0.05)";
  const dashArray = shape.dashed ? "10 6" : undefined;

  const commonProps = {
    onPointerDown: (e) => {
      e.stopPropagation();
      onPointerDown(e, shape.id);
    },
    style: { cursor: "grab", touchAction: "none" },
    "data-testid": `shape-${shape.id}`,
  };

  const bb = shapeBBox(shape);
  const dimText =
    shape.type === "rect"
      ? `${fmt(toM(shape.w))}×${fmt(toM(shape.h))}m · ${fmt(toYd(shape.w))}×${fmt(toYd(shape.h))}yd`
      : `⌀ ${fmt(toM(shape.r * 2))}m · ${fmt(toYd(shape.r * 2))}yd`;

  return (
    <g>
      {shape.type === "rect" && (
        <rect
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          stroke={stroke}
          strokeWidth={sw}
          strokeDasharray={dashArray}
          fill={fillHex}
          {...commonProps}
        />
      )}
      {shape.type === "circle" && (
        <circle
          cx={shape.cx}
          cy={shape.cy}
          r={shape.r}
          stroke={stroke}
          strokeWidth={sw}
          strokeDasharray={dashArray}
          fill={fillHex}
          {...commonProps}
        />
      )}

      {isSelected && (
        <rect
          x={bb.x - 4}
          y={bb.y - 4}
          width={bb.w + 8}
          height={bb.h + 8}
          fill="none"
          stroke="#10B981"
          strokeWidth={2}
          strokeDasharray="4 3"
          pointerEvents="none"
        />
      )}

      {/* Dimension label — positioned above the top-left corner of the shape
         by default, and offset by any user-adjusted `dimOffset` so coaches
         can drag it out of the way without changing the shape itself. */}
      {showDimensions && (() => {
        const dx = shape.dimOffset?.dx || 0;
        const dy = shape.dimOffset?.dy || 0;
        const defaultX = bb.x;
        const defaultY = bb.y - 6;
        const labelX = defaultX + dx;
        const labelY = defaultY + dy;
        const showConnector = dx !== 0 || dy !== 0;
        return (
          <g data-testid={`shape-dim-group-${shape.id}`}>
            {showConnector && (
              <line
                x1={defaultX + 3}
                y1={defaultY - 8}
                x2={labelX + 3}
                y2={labelY - 8}
                stroke="rgba(15,23,42,0.25)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}
            <DimensionLabel
              x={labelX}
              y={labelY}
              text={dimText}
              testId={`shape-dim-${shape.id}`}
              rotation={-pitchRotation}
              onPointerDown={(e) => {
                e.stopPropagation();
                if (onDimPointerDown) onDimPointerDown(e, shape.id);
              }}
            />
          </g>
        );
      })()}

      {showHandles && shape.type === "rect" && (() => {
        // Rectangle transform controls: 4 corners + 4 edge midpoints.
        // Each handle drags the opposite corner/edge as its anchor. Cursors
        // reflect the resize axis so the interaction reads correctly.
        const rx = bb.x;
        const ry = bb.y;
        const rw = bb.w;
        const rh = bb.h;
        const midX = rx + rw / 2;
        const midY = ry + rh / 2;
        const handles = [
          { mode: "resize-nw", cx: rx,      cy: ry,      cursor: "nwse-resize" },
          { mode: "resize-n",  cx: midX,    cy: ry,      cursor: "ns-resize" },
          { mode: "resize-ne", cx: rx + rw, cy: ry,      cursor: "nesw-resize" },
          { mode: "resize-e",  cx: rx + rw, cy: midY,    cursor: "ew-resize" },
          { mode: "resize-se", cx: rx + rw, cy: ry + rh, cursor: "nwse-resize" },
          { mode: "resize-s",  cx: midX,    cy: ry + rh, cursor: "ns-resize" },
          { mode: "resize-sw", cx: rx,      cy: ry + rh, cursor: "nesw-resize" },
          { mode: "resize-w",  cx: rx,      cy: midY,    cursor: "ew-resize" },
        ];
        return (
          <>
            {handles.map((h) => (
              <rect
                key={h.mode}
                x={h.cx - 5}
                y={h.cy - 5}
                width={10}
                height={10}
                fill="#FFFFFF"
                stroke="#10B981"
                strokeWidth={2}
                data-testid={`shape-handle-${h.mode}-${shape.id}`}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  onHandlePointerDown(e, h.mode, shape.id);
                }}
                style={{ cursor: h.cursor }}
              />
            ))}
          </>
        );
      })()}

      {showHandles && shape.type === "circle" && (
        <circle
          cx={bb.x + bb.w}
          cy={bb.y + bb.h}
          r={7}
          fill="#10B981"
          stroke="#FFFFFF"
          strokeWidth={2}
          data-testid={`shape-resize-handle-${shape.id}`}
          onPointerDown={(e) => {
            e.stopPropagation();
            onHandlePointerDown(e, "resize", shape.id);
          }}
          style={{ cursor: "nwse-resize" }}
        />
      )}
    </g>
  );
};

// Small pill anchored to (x, y) with x = left edge and y = baseline (bottom of pill).
// Draggable when `onPointerDown` is provided (defaults to non-interactive).
// `rotation` applies an inverse-rotation counter-turn so the text stays
// upright when the parent pitch group is rotated for horizontal views.
const DimensionLabel = ({ x, y, text, testId, onPointerDown, rotation = 0 }) => {
  const padX = 6;
  const h = 20;
  const charW = 6.5;
  const w = Math.max(48, text.length * charW + padX * 2);
  const interactive = typeof onPointerDown === "function";
  return (
    <g
      data-testid={testId}
      transform={`translate(${x} ${y - h}) rotate(${rotation})`}
      onPointerDown={onPointerDown}
      style={interactive ? { cursor: "move", touchAction: "none" } : undefined}
      pointerEvents={interactive ? "auto" : "none"}
    >
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        rx={4}
        fill="rgba(255,255,255,0.95)"
        stroke="rgba(15,23,42,0.15)"
        strokeWidth={1}
      />
      <text
        x={w / 2}
        y={h / 2}
        dy="0.35em"
        textAnchor="middle"
        fill="#0F172A"
        fontSize={11}
        fontWeight="600"
        fontFamily="IBM Plex Sans, sans-serif"
      >
        {text}
      </text>
    </g>
  );
};
